/**
 * Booking Confirmation Modal - Premium mobile-first booking confirmation
 * Shows class details and asks for confirmation before booking
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { X, Calendar, Clock, MapPin, Sparkles, Users, AlertCircle, Loader2, CheckCircle, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BookingConfirmationModal({
  session,
  studioName,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  userCredits,
  isBooked = false,
  // When the class is full and waitlist is open, the parent reuses this
  // same modal in waitlist mode — same details card, but the primary CTA
  // becomes "Join Waitlist". This avoids the previous one-tap surprise
  // (clicking the schedule button silently added members to the waitlist).
  mode = 'book',
}) {
  const navigate = useNavigate();
  const [cancelHours, setCancelHours] = useState(12);

  useEffect(() => {
    fetch(`${API_URL}/api/booking-settings/cancellation-policy`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCancelHours(d.cancellation_window_hours); })
      .catch(() => {});
  }, []);
  
  if (!isOpen || !session) return null;

  const classType = session.class_type || {};
  const creditCost = classType.credit_cost || 2;
  const hasEnoughCredits = userCredits >= creditCost;
  
  // Instructor visibility is controlled per-studio. The flag comes from the
  // session payload (`session.studio.show_instructor_to_members`) so a member
  // booking at Studio A may see instructor names while Studio B hides them.
  // Defaults to `true` (existing behavior) when the field is missing.
  const studioShowsInstructor = session.studio?.show_instructor_to_members !== false;
  const instructorName = session.instructor_name || session.instructor?.name || '';
  const showInstructorRow = studioShowsInstructor && Boolean(instructorName);
  
  let startTime;
  try {
    startTime = parseISO(session.start_time);
  } catch {
    startTime = new Date();
  }

  // Duration source-of-truth ranking:
  //   1. `session.end_time - session.start_time` (per-session override —
  //      always right if the class was scheduled with a custom length)
  //   2. `class_type.duration_minutes` (canonical column — this was the
  //      missing case that made 90-min Ashtanga classes render as 60 min)
  //   3. `class_type.duration_mins` (older/legacy field seen in Schedule.jsx)
  //   4. `class_type.duration` (whatever `duration || 60` was reading before)
  //   5. 60 as a last resort.
  let duration = null;
  try {
    if (session.end_time) {
      const endTime = parseISO(session.end_time);
      const mins = Math.round((endTime - startTime) / 60000);
      if (mins > 0 && mins < 24 * 60) duration = mins;
    }
  } catch { /* fall through */ }
  if (duration == null) {
    duration = (
      classType.duration_minutes
      || classType.duration_mins
      || classType.duration
      || 60
    );
  }

  const handleAddCredits = () => {
    onClose();
    navigate('/wallet');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl animate-scale-in overflow-hidden">
        {/* Header with gradient */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-muted-foreground hover:text-foreground transition-colors"
            data-testid="close-booking-modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Status badge for already booked */}
          {isBooked && (
            <div className="flex items-center gap-2 mb-3 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full w-fit">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Booked</span>
            </div>
          )}

          {/* Class name */}
          <h2 className="text-2xl font-heading text-foreground pr-8">
            {classType.name || 'Class'}
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Class details grid */}
          <div className="space-y-3">
            {/* Date */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium text-foreground">
                  {format(startTime, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* Time & Duration */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Time</p>
                <p className="font-medium text-foreground">
                  {format(startTime, 'h:mm a')} · {duration} min
                </p>
              </div>
            </div>

            {/* Studio */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Studio</p>
                <p className="font-medium text-foreground">{studioName}</p>
              </div>
            </div>

            {/* Instructor (per-studio toggle) */}
            {showInstructorRow && (
              <div className="flex items-center gap-3" data-testid="booking-modal-instructor-row">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Instructor</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-foreground">{instructorName}</p>
                    {(session.instructor_id || session.instructor?.id) && (
                      <button
                        type="button"
                        onClick={() => {
                          const iid = session.instructor_id || session.instructor?.id;
                          // Let the parent open its InstructorBioModal by
                          // exposing a global custom event; Schedule.jsx
                          // already has state for this. Custom event
                          // avoids drilling a new prop through the modal
                          // API and keeps the modal self-contained.
                          window.dispatchEvent(new CustomEvent('sama:open-instructor-bio', { detail: { instructor_id: iid } }));
                        }}
                        className="text-xs text-primary hover:text-primary/80 underline decoration-dotted underline-offset-2"
                        data-testid="booking-modal-view-bio-btn"
                      >
                        View Bio
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Spots & Credits row */}
            <div className="flex gap-3 pt-2">
              {/* Available spots */}
              <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-xl">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {session.available_spots > 0 ? (
                    <span className="text-foreground font-medium">{session.available_spots} spots left</span>
                  ) : (
                    <span className="text-amber-600 font-medium">Class full</span>
                  )}
                </span>
              </div>
              
              {/* Credit cost */}
              <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-xl">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  {creditCost} credit{creditCost !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Insufficient credits warning */}
          {!isBooked && mode !== 'waitlist' && !hasEnoughCredits && (
            <div className="flex items-start gap-3 p-3 bg-rose-50 text-rose-700 rounded-xl">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Insufficient credits</p>
                <p className="text-rose-600">
                  You have {userCredits} credits. This class requires {creditCost}.
                </p>
              </div>
            </div>
          )}

          {/* Cancellation note */}
          {!isBooked && mode !== 'waitlist' && hasEnoughCredits && (
            <p className="text-xs text-red-500 text-center px-4">
              The Studio reserves the right to substitute Instructors due to operational reasons. All cancellations are subject to our Cancellation Policy. 
            </p>
          )}
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-6 pt-2 space-y-3">
          {isBooked ? (
            <>
              <button
                onClick={onClose}
                className="w-full py-4 bg-primary text-white font-semibold rounded-2xl transition-all hover:bg-primary/90 active:scale-[0.98]"
              >
                Done
              </button>
            </>
          ) : mode === 'waitlist' ? (
            // Waitlist mode — same details card, but the primary CTA
            // joins the waitlist instead of booking. Credits are not
            // deducted on waitlist join, so we skip the insufficient-
            // credits warning entirely.
            <>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="w-full py-4 font-semibold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 bg-amber-500 text-white hover:bg-amber-600"
                data-testid="confirm-waitlist-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Joining...</span>
                  </>
                ) : (
                  'Join Waitlist'
                )}
              </button>
              <p className="text-xs text-muted-foreground text-center px-4">
                We'll notify you on WhatsApp the moment a spot opens up. No credits are deducted until you're confirmed.
              </p>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="w-full py-3 text-muted-foreground font-medium hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {hasEnoughCredits ? (
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="w-full py-4 font-semibold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary/90"
                  data-testid="confirm-booking-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Booking...</span>
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleAddCredits}
                  className="w-full py-4 font-semibold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary/90"
                  data-testid="add-credits-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  Add Credits to Book
                </button>
              )}
              <button
                onClick={onClose}
                disabled={isLoading}
                className="w-full py-3 text-muted-foreground font-medium hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
